"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _rbush = _interopRequireDefault(require("rbush"));

var _extent = require("../extent.js");

var _util = require("../util.js");

var _obj = require("../obj.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/structs/RBush
 */

/**
 * @typedef {Object} Entry
 * @property {number} minX MinX.
 * @property {number} minY MinY.
 * @property {number} maxX MaxX.
 * @property {number} maxY MaxY.
 * @property {Object} [value] Value.
 */

/**
 * @classdesc
 * Wrapper around the RBush by Vladimir Agafonkin.
 * See https://github.com/mourner/rbush.
 *
 * @template T
 */
var RBush =
/** @class */
function () {
  /**
   * @param {number} [opt_maxEntries] Max entries.
   */
  function RBush(opt_maxEntries) {
    /**
     * @private
     */
    this.rbush_ = new _rbush["default"](opt_maxEntries);
    /**
     * A mapping between the objects added to this rbush wrapper
     * and the objects that are actually added to the internal rbush.
     * @private
     * @type {Object<string, Entry>}
     */

    this.items_ = {};
  }
  /**
   * Insert a value into the RBush.
   * @param {import("../extent.js").Extent} extent Extent.
   * @param {T} value Value.
   */


  RBush.prototype.insert = function (extent, value) {
    /** @type {Entry} */
    var item = {
      minX: extent[0],
      minY: extent[1],
      maxX: extent[2],
      maxY: extent[3],
      value: value
    };
    this.rbush_.insert(item);
    this.items_[(0, _util.getUid)(value)] = item;
  };
  /**
   * Bulk-insert values into the RBush.
   * @param {Array<import("../extent.js").Extent>} extents Extents.
   * @param {Array<T>} values Values.
   */


  RBush.prototype.load = function (extents, values) {
    var items = new Array(values.length);

    for (var i = 0, l = values.length; i < l; i++) {
      var extent = extents[i];
      var value = values[i];
      /** @type {Entry} */

      var item = {
        minX: extent[0],
        minY: extent[1],
        maxX: extent[2],
        maxY: extent[3],
        value: value
      };
      items[i] = item;
      this.items_[(0, _util.getUid)(value)] = item;
    }

    this.rbush_.load(items);
  };
  /**
   * Remove a value from the RBush.
   * @param {T} value Value.
   * @return {boolean} Removed.
   */


  RBush.prototype.remove = function (value) {
    var uid = (0, _util.getUid)(value); // get the object in which the value was wrapped when adding to the
    // internal rbush. then use that object to do the removal.

    var item = this.items_[uid];
    delete this.items_[uid];
    return this.rbush_.remove(item) !== null;
  };
  /**
   * Update the extent of a value in the RBush.
   * @param {import("../extent.js").Extent} extent Extent.
   * @param {T} value Value.
   */


  RBush.prototype.update = function (extent, value) {
    var item = this.items_[(0, _util.getUid)(value)];
    var bbox = [item.minX, item.minY, item.maxX, item.maxY];

    if (!(0, _extent.equals)(bbox, extent)) {
      this.remove(value);
      this.insert(extent, value);
    }
  };
  /**
   * Return all values in the RBush.
   * @return {Array<T>} All.
   */


  RBush.prototype.getAll = function () {
    var items = this.rbush_.all();
    return items.map(function (item) {
      return item.value;
    });
  };
  /**
   * Return all values in the given extent.
   * @param {import("../extent.js").Extent} extent Extent.
   * @return {Array<T>} All in extent.
   */


  RBush.prototype.getInExtent = function (extent) {
    /** @type {Entry} */
    var bbox = {
      minX: extent[0],
      minY: extent[1],
      maxX: extent[2],
      maxY: extent[3]
    };
    var items = this.rbush_.search(bbox);
    return items.map(function (item) {
      return item.value;
    });
  };
  /**
   * Calls a callback function with each value in the tree.
   * If the callback returns a truthy value, this value is returned without
   * checking the rest of the tree.
   * @param {function(T): *} callback Callback.
   * @return {*} Callback return value.
   */


  RBush.prototype.forEach = function (callback) {
    return this.forEach_(this.getAll(), callback);
  };
  /**
   * Calls a callback function with each value in the provided extent.
   * @param {import("../extent.js").Extent} extent Extent.
   * @param {function(T): *} callback Callback.
   * @return {*} Callback return value.
   */


  RBush.prototype.forEachInExtent = function (extent, callback) {
    return this.forEach_(this.getInExtent(extent), callback);
  };
  /**
   * @param {Array<T>} values Values.
   * @param {function(T): *} callback Callback.
   * @private
   * @return {*} Callback return value.
   */


  RBush.prototype.forEach_ = function (values, callback) {
    var result;

    for (var i = 0, l = values.length; i < l; i++) {
      result = callback(values[i]);

      if (result) {
        return result;
      }
    }

    return result;
  };
  /**
   * @return {boolean} Is empty.
   */


  RBush.prototype.isEmpty = function () {
    return (0, _obj.isEmpty)(this.items_);
  };
  /**
   * Remove all values from the RBush.
   */


  RBush.prototype.clear = function () {
    this.rbush_.clear();
    this.items_ = {};
  };
  /**
   * @param {import("../extent.js").Extent} [opt_extent] Extent.
   * @return {import("../extent.js").Extent} Extent.
   */


  RBush.prototype.getExtent = function (opt_extent) {
    var data = this.rbush_.toJSON();
    return (0, _extent.createOrUpdate)(data.minX, data.minY, data.maxX, data.maxY, opt_extent);
  };
  /**
   * @param {RBush} rbush R-Tree.
   */


  RBush.prototype.concat = function (rbush) {
    this.rbush_.load(rbush.rbush_.all());

    for (var i in rbush.items_) {
      this.items_[i] = rbush.items_[i];
    }
  };

  return RBush;
}();

var _default = RBush;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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